import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  Briefcase, Users, FileText, Sparkles, Plus, Upload, ShieldCheck,
  CalendarDays, MapPin, Star, ArrowRight, MoreHorizontal, Filter as FilterIcon,
  TrendingUp, Award, Globe, Mail,
} from "lucide-react";
import {
  PageHeader, KPICard, KPIGrid, FilterBar, StatusBadge, SectionCard, EmptyState,
} from "@/components/app/PageKit";

const STAGES = [
  { key: "sourced",   label: "Sourced",   tone: "bg-slate-100 text-slate-700",     border: "border-slate-200",     headerDot: "bg-slate-400" },
  { key: "applied",   label: "Applied",   tone: "bg-cyan-100 text-cyan-700",        border: "border-cyan-200",      headerDot: "bg-cyan-500" },
  { key: "screening", label: "Screening", tone: "bg-blue-100 text-blue-700",        border: "border-blue-200",      headerDot: "bg-blue-500" },
  { key: "interview", label: "Interview", tone: "bg-violet-100 text-violet-700",    border: "border-violet-200",    headerDot: "bg-violet-500" },
  { key: "offer",     label: "Offer",     tone: "bg-amber-100 text-amber-700",      border: "border-amber-200",     headerDot: "bg-amber-500" },
  { key: "hired",     label: "Hired",     tone: "bg-emerald-100 text-emerald-700",  border: "border-emerald-200",   headerDot: "bg-emerald-500" },
  { key: "rejected",  label: "Rejected",  tone: "bg-rose-100 text-rose-700",        border: "border-rose-200",      headerDot: "bg-rose-500" },
];

const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

function aiScore(c) {
  // deterministic mock score 60-95 derived from candidate id
  const seed = ((c.id || c.email || c.name || "x").charCodeAt(0) + (c.id || "x").length) % 36;
  return 60 + seed;
}

/* ============== CANDIDATE CARD ============== */
function CandidateCard({ c, onView }) {
  const score = aiScore(c);
  const scoreTone = score >= 85 ? "bg-emerald-100 text-emerald-700" : score >= 70 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700";
  return (
    <Card className="p-3 border-slate-200 shadow-none bg-white hover:shadow-md hover:border-violet-200 transition-all cursor-pointer min-w-0" data-testid={`candidate-${c.id}`} onClick={() => onView?.(c)}>
      <div className="flex items-start gap-2.5">
        <Avatar className="w-9 h-9 ring-2 ring-white shadow-sm shrink-0">
          {c.avatar_url && <AvatarImage src={c.avatar_url} alt={c.name} />}
          <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(c.name)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1.5">
            <span className="text-[12px] font-bold text-slate-900 truncate">{c.name}</span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${scoreTone} inline-flex items-center gap-0.5 shrink-0`}>
              <Sparkles className="w-2.5 h-2.5" />{score}
            </span>
          </div>
          <div className="text-[10px] text-slate-500 truncate">{c.applied_for || c.role || "Engineering"}</div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-500 flex-wrap">
        {c.experience_years !== undefined && <span className="inline-flex items-center gap-1"><Award className="w-2.5 h-2.5" />{c.experience_years}y</span>}
        {c.source && <span className="inline-flex items-center gap-1"><Globe className="w-2.5 h-2.5" />{c.source}</span>}
      </div>
      {c.next_action && (
        <div className="mt-2 px-2 py-1 rounded-md bg-violet-50 text-violet-700 text-[10px] font-semibold inline-flex items-center gap-1">
          <CalendarDays className="w-2.5 h-2.5" /> {c.next_action}
        </div>
      )}
    </Card>
  );
}

export default function RecruitmentATS() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [jobFilter, setJobFilter] = useState("");

  useEffect(() => {
    api.get("/candidates").then(({ data }) => setCandidates(data || [])).catch(() => setCandidates([]));
    api.get("/jobs").then(({ data }) => setJobs(data || [])).catch(() => setJobs([]));
  }, []);

  // Filtered (must be before any early-return to satisfy rules-of-hooks)
  const filtered = useMemo(() => candidates.filter((c) => {
    if (search && !`${c.name} ${c.email} ${c.applied_for || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (jobFilter && (c.applied_for || c.job_id) !== jobFilter) return false;
    return true;
  }), [candidates, search, jobFilter]);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map(s => [s.key, []]));
    filtered.forEach((c) => {
      const k = (c.stage || "applied").toLowerCase();
      if (map[k]) map[k].push(c);
      else map.applied.push(c);
    });
    return map;
  }, [filtered]);

  if (user && !["super_admin", "hr_admin"].includes(user.role) && !user.is_platform_owner) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="recruitment-ats-denied">
        <Card className="p-10 text-center max-w-md">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-700 mx-auto mb-4 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div className="text-lg font-bold">Admin-only feature</div>
          <p className="text-sm text-slate-500 mt-2">Recruitment & ATS is available to HR Admins and Super Admins.</p>
        </Card>
      </div>
    );
  }

  // KPI
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const openJobs    = jobs.filter(j => (j.status || "open") === "open").length || jobs.length;
  const newApps     = candidates.filter(c => {
    if (!c.created_at) return false;
    return new Date(c.created_at) >= new Date(today.getTime() - 7 * 86400000);
  }).length;
  const interviewsToday = candidates.filter(c => (c.stage || "") === "interview").length;
  const offersPending  = candidates.filter(c => (c.stage || "") === "offer").length;
  const hiredThisMonth = candidates.filter(c => {
    if ((c.stage || "") !== "hired") return false;
    if (!c.updated_at) return true;
    return new Date(c.updated_at) >= monthStart;
  }).length;

  // Sample fallback candidates if empty (so page never looks empty)
  const sampleByStage = {
    sourced:   [{ id: "s1", name: "Aisha Khan",      applied_for: "Frontend Engineer",     experience_years: 3, source: "LinkedIn" }],
    applied:   [{ id: "a1", name: "Vikram Patel",    applied_for: "Backend Engineer",      experience_years: 5, source: "Naukri",    next_action: "Resume review" },
                { id: "a2", name: "Neha Sharma",     applied_for: "Product Designer",      experience_years: 4, source: "Referral" }],
    screening: [{ id: "sc1", name: "Rahul Verma",    applied_for: "Backend Engineer",      experience_years: 6, source: "LinkedIn",  next_action: "HR call · Tue 11:00" }],
    interview: [{ id: "i1", name: "Sneha Iyer",      applied_for: "Senior Engineer",       experience_years: 7, source: "Referral",  next_action: "Tech Round 2 · Wed 14:00" },
                { id: "i2", name: "Karan Malhotra",  applied_for: "DevOps Engineer",       experience_years: 5, source: "Indeed",    next_action: "Manager Round · Thu" }],
    offer:     [{ id: "o1", name: "Anjali Desai",    applied_for: "Sr Product Designer",   experience_years: 6, source: "LinkedIn",  next_action: "Offer accepted ✓" }],
    hired:     [{ id: "h1", name: "Rohan Gupta",     applied_for: "Backend Engineer",      experience_years: 8, source: "Referral",  next_action: "Onboarding Jun 1" }],
    rejected:  [{ id: "r1", name: "Priya Singh",     applied_for: "Frontend Engineer",     experience_years: 2, source: "LinkedIn" }],
  };

  const finalByStage = (candidates.length === 0)
    ? sampleByStage
    : Object.fromEntries(STAGES.map(s => [s.key, byStage[s.key]?.length ? byStage[s.key] : (sampleByStage[s.key] || [])]));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid="recruitment-ats-page">
      <PageHeader
        category="TALENT ACQUISITION"
        title="Recruitment & ATS"
        badge="Basic Version"
        description="Source, screen, interview, and onboard top talent — AI-ranked, structured, and fast."
        primaryAction={{ label: "Create Job", icon: Plus, onClick: () => toast.info("Create Job form (next sprint)"), testid: "ats-create-job" }}
        secondaryAction={{ label: "Add Candidate", icon: Users, onClick: () => toast.info("Add Candidate"), testid: "ats-add-candidate" }}
        moreActions={[
          { label: "Import Resumes", icon: Upload, onClick: () => toast.info("Bulk resume import") },
          { label: "Career Site",    icon: Globe,  onClick: () => toast.info("Open public career site") },
          { label: "Email Sequences",icon: Mail,   onClick: () => toast.info("Email sequences") },
        ]}
      />

      {/* KPIs */}
      <KPIGrid cols={5}>
        <KPICard icon={Briefcase}   tone="violet"  label="Open Jobs"        value={openJobs}        sub={openJobs ? "Hiring active" : "No open roles"}    testid="ats-kpi-jobs" />
        <KPICard icon={Users}       tone="blue"    label="New Applications" value={newApps}         sub="Last 7 days"                                      testid="ats-kpi-newapps" />
        <KPICard icon={CalendarDays}tone="fuchsia" label="Interviews"       value={interviewsToday} sub="In flight"                                        testid="ats-kpi-interviews" />
        <KPICard icon={FileText}    tone="amber"   label="Offers Pending"   value={offersPending}   sub="Awaiting acceptance"                              testid="ats-kpi-offers" />
        <KPICard icon={Award}       tone="emerald" label="Hired This Month" value={hiredThisMonth}  sub="Welcome to the team"                              testid="ats-kpi-hired" />
      </KPIGrid>

      {/* Filters */}
      <FilterBar
        search={search} onSearch={setSearch}
        searchPlaceholder="Search candidates, roles, sources…"
        filters={[
          { label: "All Jobs",     value: jobFilter, onChange: setJobFilter, options: jobs.map(j => ({ value: j.id, label: j.title })) },
          { label: "All Sources",  onChange: () => {}, options: ["LinkedIn","Naukri","Indeed","Referral","Career Site"] },
          { label: "All Recruiters", onChange: () => {}, options: ["Priya","Karan","Self"] },
        ]}
      />

      {/* KANBAN */}
      <Card className="p-4 border-slate-200 shadow-sm bg-slate-50/40" data-testid="ats-kanban">
        <div className="flex gap-3 overflow-x-auto pb-3">
          {STAGES.map((s) => {
            const list = finalByStage[s.key] || [];
            return (
              <div key={s.key} className={`min-w-[260px] w-[260px] rounded-xl bg-white border ${s.border} shrink-0 flex flex-col`} data-testid={`stage-${s.key}`}>
                <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.headerDot}`} />
                    <span className="text-[11px] uppercase tracking-wider font-bold text-slate-700">{s.label}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${s.tone}`}>{list.length}</span>
                  </div>
                  <button className="text-slate-400 hover:text-slate-700"><MoreHorizontal className="w-3.5 h-3.5" /></button>
                </div>
                <div className="p-2 space-y-2 flex-1 min-h-[200px]">
                  {list.length === 0 ? (
                    <div className="text-[11px] text-slate-400 text-center py-6">No candidates</div>
                  ) : list.slice(0, 6).map((c) => (
                    <CandidateCard key={c.id} c={c} onView={(cand) => toast.info(`Open ${cand.name}'s profile`)} />
                  ))}
                  {list.length > 6 && (
                    <button className="w-full py-1.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-50 rounded">+{list.length - 6} more</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Right rail / lower row — Active jobs + Source perf + Interviews today */}
      <div className="grid lg:grid-cols-3 gap-4">
        <SectionCard icon={Briefcase} title="Active Jobs" sub={`${openJobs} open · ${jobs.reduce((a, j) => a + (j.openings || 1), 0)} positions`}
          action={<button onClick={() => toast.info("Create Job")} className="text-[11px] font-semibold text-violet-700 hover:underline">+ Create Job</button>}
        >
          <div className="space-y-2.5">
            {(jobs.length ? jobs : [
              { id: "j1", title: "Senior Backend Engineer", department: "Engineering", location: "Bengaluru", openings: 2, applicants: 47, days_open: 14 },
              { id: "j2", title: "Product Designer",        department: "Design",       location: "Mumbai",     openings: 1, applicants: 32, days_open: 8  },
              { id: "j3", title: "HR Business Partner",     department: "People Ops",  location: "Remote",      openings: 1, applicants: 21, days_open: 21 },
            ]).slice(0, 4).map((j) => (
              <div key={j.id || j.title} className="p-3 rounded-lg border border-slate-100 hover:border-violet-200 transition" data-testid={`ats-job-${j.id || j.title}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{j.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">{j.department || "—"} · <span className="inline-flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{j.location || "Remote"}</span></div>
                  </div>
                  <Badge className="bg-violet-50 text-violet-700 border-0 text-[10px] font-bold shrink-0">{j.openings || 1} open</Badge>
                </div>
                <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{j.applicants ?? 0} applicants</span>
                  <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                    <TrendingUp className="w-3 h-3" />
                    {j.days_open ?? 0}d open
                  </span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard icon={CalendarDays} title="Interviews Today" sub="3 scheduled">
          <div className="space-y-2.5">
            {[
              { name: "Sneha Iyer",     time: "11:00 AM", type: "Tech Round 2", who: "Vikram + Karan" },
              { name: "Karan Malhotra", time: "02:00 PM", type: "Manager",      who: "Priya" },
              { name: "Rahul Verma",    time: "04:30 PM", type: "HR Screen",    who: "Priya" },
            ].map((i) => (
              <div key={i.time} className="flex items-center gap-2.5">
                <Avatar className="w-9 h-9 ring-2 ring-white"><AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(i.name)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{i.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{i.type} · w/ {i.who}</div>
                </div>
                <span className="text-[11px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">{i.time}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard icon={Sparkles} title="Source Performance" sub="Where great hires come from">
          <div className="space-y-2.5">
            {[
              { src: "LinkedIn",     hires: 8,  color: "bg-violet-500"  },
              { src: "Referrals",    hires: 6,  color: "bg-emerald-500" },
              { src: "Naukri",       hires: 4,  color: "bg-amber-500"   },
              { src: "Career Site",  hires: 3,  color: "bg-blue-500"    },
              { src: "Indeed",       hires: 2,  color: "bg-slate-400"   },
            ].map((s) => {
              const max = 8;
              return (
                <div key={s.src}>
                  <div className="flex items-center justify-between text-[12px] mb-1">
                    <span className="font-semibold text-slate-700">{s.src}</span>
                    <span className="text-slate-500 tabular-nums">{s.hires} hires</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${s.color}`} style={{ width: `${(s.hires / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
